// Browser Web Push client (Phase 46, D-17).
//
// Every browser global (window, navigator, Notification, PushManager) is touched ONLY inside a
// function body behind a guard, never at module scope, so this file is safe to import during SSR
// and from node-env tests. Callers must also skip all of it inside the embedded preview iframe
// (`isEmbeddedPreview()`, read in an effect or handler).
//
// The service worker is /sw.js at scope '/' (46-07). It has no `pushsubscriptionchange` handler,
// so it cannot re-subscribe on its own: `syncExistingPushSubscription()` re-POSTs the current
// subscription from the page instead. Registering the worker happens only in `subscribeToPush()`,
// i.e. only after the user accepted the soft ask; nothing registers it on page load.

import { PUSH_PROMPT_SNOOZE_DAYS } from "@shared/notifications";
// Relative imports on purpose: vitest (node env) has no `@/` alias, and the pure helpers here are
// unit-tested.
import { detectIos, detectStandalone } from "../hooks/useInstallPrompt";
import {
  getPushPublicKey,
  removePushSubscription,
  savePushSubscription,
  type PushSubscriptionJSONBody,
} from "../api/notificationService";

export type PushSupport = "supported" | "unsupported" | "ios_needs_install";
export type PushPermissionState = NotificationPermission | "unsupported";
export type PushSoftAskState = "ask" | "ios_install" | "hidden";
export type SubscribeResult = "granted" | "denied" | "unsupported" | "error";

const SW_URL = "/sw.js";
const SW_SCOPE = "/";
const DAY_MS = 24 * 60 * 60 * 1000;

// ─── Pure helpers ───

/** VAPID public keys arrive as unpadded base64url; PushManager wants the raw bytes. */
export const urlBase64ToUint8Array = (b64: string): Uint8Array => {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = globalThis.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

/**
 * Whether the soft ask should render, and in which form. The snooze applies to the iOS install
 * instruction too, since dismissing it writes the same `pushPromptDismissedAt`.
 */
export const shouldShowPushSoftAsk = ({
  support,
  permission,
  subscribed,
  dismissedAt,
  now = new Date(),
}: {
  support: PushSupport;
  permission: PushPermissionState;
  subscribed: boolean;
  dismissedAt: string | Date | null | undefined;
  now?: Date;
}): PushSoftAskState => {
  if (support === "unsupported") return "hidden";

  if (dismissedAt) {
    const at = new Date(dismissedAt).getTime();
    if (!Number.isNaN(at) && now.getTime() - at < PUSH_PROMPT_SNOOZE_DAYS * DAY_MS) return "hidden";
  }

  // iOS Safari outside a Home Screen install has no Notification API at all, so the permission
  // is meaningless here; the only useful thing to say is "install first".
  if (support === "ios_needs_install") return "ios_install";

  if (permission !== "default") return "hidden";
  if (subscribed) return "hidden";
  return "ask";
};

// ─── Browser capability ───

const hasPushApis = (): boolean =>
  typeof window !== "undefined" &&
  typeof navigator !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const getPushSupport = (): PushSupport => {
  if (typeof window === "undefined") return "unsupported";
  if (detectIos() && !detectStandalone()) return "ios_needs_install";
  return hasPushApis() ? "supported" : "unsupported";
};

export const getPermission = (): PushPermissionState => {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return window.Notification.permission;
};

/** The current device's subscription, if the worker is already registered. Never registers. */
export const getDeviceSubscription = async (): Promise<PushSubscription | null> => {
  if (!hasPushApis()) return null;
  try {
    const registration = await navigator.serviceWorker.getRegistration(SW_SCOPE);
    if (!registration) return null;
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
};

const toBody = (sub: PushSubscription): PushSubscriptionJSONBody | null => {
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return null;
  return { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } };
};

const sameKey = (current: ArrayBuffer | null, next: Uint8Array): boolean => {
  if (!current) return false;
  const a = new Uint8Array(current);
  if (a.length !== next.length) return false;
  return a.every((byte, i) => byte === next[i]);
};

// ─── Subscribe / sync / unsubscribe ───

/**
 * Asks for the permission and subscribes this device.
 *
 * MUST be called synchronously from a click handler: `Notification.requestPermission()` is the
 * first thing after the (synchronous) support check, with no await before it, because Safari and
 * Firefox only honour the prompt while the user activation from the click is still live.
 */
export const subscribeToPush = async (): Promise<SubscribeResult> => {
  if (getPushSupport() !== "supported") return "unsupported";
  const permissionPromise = window.Notification.requestPermission();

  try {
    const permission = await permissionPromise;
    if (permission !== "granted") return "denied";

    await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
    const registration = await navigator.serviceWorker.ready;

    const keyRes = await getPushPublicKey();
    if (!keyRes.ok || !keyRes.data?.publicKey) return "error";

    // Reuse an existing subscription when it was made with the same key; PushManager rejects a
    // subscribe() with a different key while one exists, so a subscription under a rotated key is
    // dropped first.
    const applicationServerKey = urlBase64ToUint8Array(keyRes.data.publicKey);
    let subscription = await registration.pushManager.getSubscription();
    if (subscription && !sameKey(subscription.options?.applicationServerKey ?? null, applicationServerKey)) {
      try {
        await subscription.unsubscribe();
      } catch {
        // Could not drop it: subscribe() below reports the real failure.
      }
      subscription = null;
    }
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as BufferSource,
      });
    }

    const body = toBody(subscription);
    if (!body) return "error";
    const saved = await savePushSubscription(body);
    return saved.ok ? "granted" : "error";
  } catch {
    return "error";
  }
};

/**
 * Re-POSTs this device's existing subscription so the server row stays fresh (the SW cannot
 * re-subscribe itself). Never registers the worker and never prompts.
 */
export const syncExistingPushSubscription = async (): Promise<boolean> => {
  if (getPermission() !== "granted") return false;
  const subscription = await getDeviceSubscription();
  if (!subscription) return false;
  const body = toBody(subscription);
  if (!body) return false;
  const res = await savePushSubscription(body);
  return res.ok;
};

export const unsubscribeFromPush = async (): Promise<boolean> => {
  const subscription = await getDeviceSubscription();
  if (!subscription) return true;
  const { endpoint } = subscription;
  try {
    await subscription.unsubscribe();
  } catch {
    // Still tell the server: a row it keeps would only 410 on the next send.
  }
  const res = await removePushSubscription({ endpoint });
  return res.ok;
};

/**
 * Resolves with the promise's value, or with `fallback` once `ms` passes, whichever comes first.
 * A rejection also resolves with `fallback`. Never rejects.
 */
export const settleWithin = async <T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<T>((resolve) => {
    timer = setTimeout(() => resolve(fallback), ms);
  });
  try {
    return await Promise.race([promise.catch(() => fallback), timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

export const SIGN_OUT_PUSH_TIMEOUT_MS = 3000;

/**
 * Sign-out teardown: drops this device's subscription so the next person on this browser does
 * not receive the previous user's pushes. Must run BEFORE `auth_token` is removed, because the
 * server-side remove route is authenticated. Bounded and never throws, so sign-out never waits on
 * a hung service worker or network call for more than `timeoutMs`.
 */
export const unsubscribeFromPushOnSignOut = async (timeoutMs = SIGN_OUT_PUSH_TIMEOUT_MS): Promise<boolean> =>
  settleWithin(unsubscribeFromPush(), timeoutMs, false);
